// اللَّهُ لَا إِلَٰهَ إِلَّا هُوَ الْحَيُّ الْقَيُّومُ ۚ لَا تَأْخُذُهُ سِنَةٌ وَلَا نَوْمٌ ۚ
// لَهُ مَا فِي السَّمَاوَاتِ وَمَا فِي الْأَرْضِ ۗ مَنْ ذَا الَّذِي يَشْفَعُ عِنْدَهُ إِلَّا بِإِذْنِهِ ۚ
// يَعْلَمُ مَا بَيْنَ أَيْدِيهِمْ وَمَا خَلْفَهُمْ ۖ وَلَا يُحِيطُونَ بِشَيْءٍ مِنْ عِلْمِهِ إِلَّا بِمَا شَاءَ ۚ
// وَسِعَ كُرْسِيُّهُ السَّمَاوَاتِ وَالْأَرْضَ ۖ وَلَا يَئُودُهُ حِفْظُهُمَا ۚ وَهُوَ الْعَلِيُّ الْعَظِيمُ
// (Bakara 255)

import { apiClient } from './client';
import { registerFormSectionStyle } from 'elpo-core-renderer';
import { aktifDil } from 'elpo-core-renderer';

export interface BrandSidebarItem {
  readonly label: string;
  readonly icon: string | null;
  readonly path: string | null;
  readonly entity: string | null;       // "{app}/{Object}" formatinda
  readonly subtitle: string | null;
  readonly badge: number | null;
  readonly children: BrandSidebarItem[] | null;
  /** Brand'li urunlerde detay sayfasinda gosterilecek alanlar (whitelist). Yoksa tum alanlar. */
  readonly fieldVisibility?: readonly string[];
  /**
   * Aktif sirketin abone oldugu modul/kanal anahtari (orn. einvoice/earchive/despatch/esevoucher/creditnote).
   * Tanimliysa: yalniz aktif sirkette o modul acikken gosterilir (eportal=entegrator urun havuzu,
   * diger app=lisans). Tanimsizsa her zaman gosterilir. Moduller cozulemediginde kisitlama YOK.
   */
  readonly moduleKey?: string;
}

export interface BrandConfig {
  readonly subdomain: string | null;
  readonly displayName: string;
  readonly tagline: string | null;
  readonly primaryColor: string;
  readonly accentColor: string;
  readonly logo: string | null;
  readonly favicon: string | null;
  readonly homeRoute: string;
  readonly navPreset: string | null;
  readonly loginGreeting: string | null;
  readonly loginSubtitle: string | null;
  readonly footerText: string | null;
  readonly loginFeatures: string[];
  readonly sidebar: BrandSidebarItem[];
  readonly entityLabels: Record<string, Record<string, string>>;
  // İlk-kez entegratör onboarding sekmesi göster (e-belge portalı gibi entegratör-destekli app'ler).
  readonly integratorOnboarding?: boolean;
  // Login sol panelinde logo ile başlık arasına yazılan kısa ürün etiketi (örn. "E-Entegratör").
  readonly panelTag?: string | null;
  // "branded" → login yan paneli ürünün KENDİ kimliğini gösterir (ekosistem vitrini yerine).
  readonly loginPanel?: string | null;
  // true → ürünün subdomain'inde sol menüde YALNIZ kendi grubu + platform grupları görünür;
  // başka ürünün modül grupları (CRM/Satış/Stok…) gizlenir.
  readonly navIsolated?: boolean;
  // "underline" → form bölüm başlıkları alt-çizgili sade stil (SmartŞarj dili); yoksa mevcut görünüm.
  readonly formStyle?: string | null;
  // true → burası PLATFORM konsoludur, satılan bir uygulama değil (elpocore/elpocoredev/elpocoretest).
  // Sol menüde hiçbir app'in iş modülü (CRM, Satış, Stok, CX…) görünmez; yalnız platform araçları kalır.
  // Bu bayrak olmadan platform adresi kendini SmartERP sanıyordu: bilinen subdomain listesinde
  // olmadığı için DEFAULT_BRAND uygulanıyor (adı "SmartERP"), aktif app varsayılanı da 'smarterp'
  // kaldığı için SmartERP'in tüm menüsü ve /entity/smarterp/... ekranları açılıyordu.
  readonly platformKonsolu?: boolean;
}

const DEFAULT_BRAND: BrandConfig = {
  subdomain: null,
  displayName: 'SmartERP',
  tagline: 'Yapay zeka destekli ERP',
  primaryColor: '#003B5C',
  accentColor: '#FF6A13',
  logo: '/elpo-icon.png',
  // ELPO'nun yuvarlak "e" amblemi — markası kendi ikonunu bildirmeyen her ürünün varsayılanı.
  favicon: '/elpo-icon.png',
  homeRoute: '/admin/dashboard',
  navPreset: null,
  loginGreeting: null,
  loginSubtitle: null,
  footerText: null,
  loginFeatures: [],
  sidebar: [],
  entityLabels: {},
};

// eportal e-Belge menüsü — app'in kendi sidebar'ı (yikamatik gibi branded).
// Bunun varlığı isBrandedProduct=true yapar → smarterp CRM/Satış/Satınalma platform
// grupları gizlenir, sadece bu e-Belge menüsü görünür.
// Eski ELPO Portal menü yapısına göre düzenlendi (belge tipi + Yeni Oluştur / Taslaklar / Gelen / Giden / Sorgu).
// Yalnız ElpoCore'da GERÇEKTEN var olan route'lara bağlı (kırık link yok).
// Eski ELPO Portal yapısı: Ana Sayfa (tek öğe) → Belge İşlemleri (kart) → Gelen/Giden (sekmeli) →
// Taslaklar, Onay İşlemleri, Rapor İşlemleri, Hata Takibi, Yükleme (tek öğe) → Araçlar, Ayarlar (grup).
// Yalnız GERÇEK var olan route'lara bağlı (stub/kırık link yok); Onay İşlemleri + Rapor İşlemleri +
// Zarf Sorgula portalyeni paritesi için eklendi (entegratör REST'e bağlı sayfalar).
const EPORTAL_SIDEBAR: BrandSidebarItem[] = [
  { label: 'Ana Sayfa', icon: 'LayoutDashboard', path: '/einvoice/ebelge-dashboard', entity: null, subtitle: null, badge: null, children: null },
  // Kullanıcı kararı 30.09.2026: "Yeni e-Belge" ve "Taslaklar" sol menüde YOK. Yeni belge düğmesi ve
  // Taslaklar Giden Kutusu'nun kanal sekmelerinin yanında; yeni belge ayrıca üst çubuktaki + menüsünde.
  // Alt menü YOK (kullanıcı kararı 2026-08-10): tıklayınca doğrudan sekmeli Gelen/Giden Kutusu sayfasına
  // gider; belge tipleri o sayfadaki sekmelerde. children:null → AppLayout tek öğe render eder (chevron/alt menü yok).
  { label: 'Gelen Kutusu', icon: 'Inbox', path: '/eportal/inbox', entity: null, subtitle: 'E-Fatura / E-İrsaliye', badge: null, children: null },
  { label: 'Giden Kutusu', icon: 'Send', path: '/eportal/outbox', entity: null, subtitle: 'Yeni belge · Taslaklar · Gönderilenler', badge: null, children: null },
  // Tek başına duran alt-menüsüz öğeler tek grupta toplandı (kullanıcı kararı 2026-06-23) — menü derli toplu.
  {
    label: 'Yönetim', icon: 'LayoutList', path: '/eportal/approvals', entity: null, subtitle: 'Onay · Rapor · Hata · Yükleme', badge: null,
    children: [
      { label: 'Onay İşlemleri', icon: 'ClipboardCheck', path: '/eportal/approvals', entity: null, subtitle: null, badge: null, children: null },
      { label: 'Rapor İşlemleri', icon: 'FileBarChart', path: '/eportal/reports', entity: null, subtitle: null, badge: null, children: null },
      { label: 'Hata Takibi', icon: 'TriangleAlert', path: '/einvoice/faults', entity: null, subtitle: null, badge: null, children: null },
      { label: 'Yükleme Araçları', icon: 'Upload', path: '/einvoice/upload', entity: null, subtitle: null, badge: null, children: null },
    ],
  },
  {
    label: 'Ödeme İşlemleri', icon: 'Coins', path: '/eportal/payments', entity: null, subtitle: 'Kontör satın al', badge: null,
    children: [
      { label: 'Kontör Satın Al', icon: 'CreditCard', path: '/eportal/buy-credit', entity: null, subtitle: null, badge: null, children: null },
    ],
  },
  {
    label: 'Araçlar', icon: 'Wrench', path: '/eportal/tools', entity: null, subtitle: 'Sorgu · Bildirim · Destek', badge: null,
    children: [
      { label: 'Mükellef Sorgu', icon: 'UserSearch', path: '/einvoice/customer-query', entity: null, subtitle: null, badge: null, children: null },
      { label: 'Zarf / Durum Sorgu', icon: 'PackageSearch', path: '/einvoice/questioning', entity: null, subtitle: null, badge: null, children: null },
      { label: 'Zarf Sorgula', icon: 'Package', path: '/eportal/envelope-query', entity: null, subtitle: null, badge: null, children: null },
      // Referans portal menüsünde olan, eportal'da sayfası hazır ama menüden ulaşılamayan ekranlar (PARITE 41, 02.10.2026).
      { label: 'Bildirimler', icon: 'Bell', path: '/eportal/notifications', entity: null, subtitle: null, badge: null, children: null },
      // Destek tek kapı (05.10.2026): ELPO'nun Cortex'ine ElpoServicesApi kapısından; üründe ayrı destek tablosu yok.
      { label: "ELPO'ya Bildir", icon: 'LifeBuoy', path: '/eportal/support/report', entity: null, subtitle: null, badge: null, children: null },
      { label: 'Taleplerim', icon: 'Inbox', path: '/eportal/support/requests', entity: null, subtitle: null, badge: null, children: null },
      { label: 'GİB Başvuru Durumu', icon: 'FileCheck', path: '/eportal/gib-applications', entity: null, subtitle: null, badge: null, children: null },
    ],
  },
  {
    label: 'Ayarlar', icon: 'Settings', path: '/eportal/settings', entity: null, subtitle: 'Gönderici, alıcı, ön ek, şablon, kimlik', badge: null,
    children: [
      { label: 'Gönderici Profilleri', icon: 'Building2', path: '/einvoice/sender-profiles', entity: null, subtitle: null, badge: null, children: null },
      { label: 'Alıcı / Cari', icon: 'Contact', path: '/einvoice/receivers', entity: null, subtitle: null, badge: null, children: null },
      { label: 'Ön Ek Yönetimi', icon: 'Hash', path: '/einvoice/prefixes', entity: null, subtitle: null, badge: null, children: null },
      { label: 'Görsel Şablonlar', icon: 'Palette', path: '/einvoice/templates', entity: null, subtitle: null, badge: null, children: null },
      { label: 'e-Belge Kimlik', icon: 'Key', path: '/einvoice/credentials', entity: null, subtitle: null, badge: null, children: null },
      // Mali mühür (USB token) ile GİB ürün aktivasyonu — ELPO İmza masaüstü ajanı (elpoimza://).
      { label: 'Mükellef Aktivasyonu', icon: 'ShieldCheck', path: '/einvoice/activation', entity: null, subtitle: null, badge: null, children: null },
      // Hizmet sözleşmesi mali mühürle imzalama (firmaya göre backend'de üretilir).
      { label: 'Hizmet Sözleşmesi', icon: 'FileSignature', path: '/einvoice/contract', entity: null, subtitle: null, badge: null, children: null },
      { label: 'Firma Bilgileri', icon: 'Building', path: '/eportal/company-info', entity: null, subtitle: null, badge: null, children: null },
      { label: 'Yetkili Kişiler', icon: 'Users', path: '/eportal/authorized-persons', entity: null, subtitle: null, badge: null, children: null },
      { label: 'Muhasebeci (SMMM)', icon: 'Calculator', path: '/eportal/accountants', entity: null, subtitle: null, badge: null, children: null },
      { label: 'Hazır Notlar', icon: 'ScrollText', path: '/eportal/extra-info', entity: null, subtitle: null, badge: null, children: null },
      { label: 'Belge Etiketleri', icon: 'Tag', path: '/eportal/tags', entity: null, subtitle: null, badge: null, children: null },
      { label: 'API Anahtarları', icon: 'KeyRound', path: '/eportal/api-keys', entity: null, subtitle: null, badge: null, children: null },
      // Referans "Kullanıcı Bilgileri" ve "Kullanıcı Yönetimi" karşılıkları (PARITE 50): sayfalar hazırdı, menüde yoktu.
      { label: 'Kullanıcı Bilgileri', icon: 'UserCircle', path: '/profile', entity: null, subtitle: null, badge: null, children: null },
      { label: 'Kullanıcı Yönetimi', icon: 'UserCog', path: '/settings/user-access', entity: null, subtitle: null, badge: null, children: null },
    ],
  },
];

// Subdomain'den senkron çıkarsama — API gelene kadar FOUC yaşanmasın.
// Anahtar değerler appsettings.json SubdomainBrand ile birebir aynı kalmalı.
function brandFromSubdomain(): BrandConfig {
  if (typeof window === 'undefined') return DEFAULT_BRAND;
  const host = window.location.hostname.toLowerCase();
  // LOCAL GELISTIRME: localhost hicbir dala uymuyor ve DEFAULT_BRAND (SmartERP) donuyordu — proje
  // kurali "uygulamayi hep local'den calistir" oldugu icin platform konsolu local'de kendini
  // SmartERP saniyordu (denetim bulgusu 2026-08-31). Vite env ile hangi urun oldugu bildirilebilir:
  //   VITE_ELPO_BRAND=elpocore npm run dev
  // (Vite varsayilan oneki VITE_; onsuz degisken istemciye HIC gecmez.)
  const yerelMi = host === 'localhost' || host === '127.0.0.1' || host === '[::1]';
  const envMarka = (import.meta.env.VITE_ELPO_BRAND as string | undefined)?.toLowerCase();
  const sub = yerelMi && envMarka
    ? envMarka
    : (host.split('.')[0]?.replace(/(dev|test)$/i, '') ?? '');
  if (sub === 'yikamatik') {
    return {
      ...DEFAULT_BRAND,
      subdomain: 'yikamatik',
      displayName: 'Yıkamatik',
      tagline: 'Oto yıkama randevu ve operasyon yönetimi',
      primaryColor: '#0891b2',
      accentColor: '#10b981',
      logo: '/brand/yikamatik/logo.svg',
      favicon: '/brand/yikamatik/favicon.svg',
      // DİKKAT: Yıkamatik EKRANLARI 27.09.2026'da kapatıldı (App.tsx'te route'lar yorumda,
      // kullanıcı kararı: "şimdilik yorum satırı yap, orada işimiz yok"). Bu adres şu an
      // karşılıksız — yikamatik alt alan adıyla girilirse boş sayfaya düşer. Ekranlar geri
      // açılınca burası kendiliğinden çalışır; açmadan bu markayı kullanıma sunma.
      homeRoute: '/yikamatik/today',
      loginGreeting: "Yıkamatik'e hoş geldin",
      loginSubtitle: 'Plakadan müşteri açılır, randevu 3 dakikada hazır',
      footerText: '© Yıkamatik · Oto yıkama yönetim sistemi',
    };
  }
  // ELPO CX — sunucudaki marka kaydının (core/branding/subdomain-brands.json) İSTEMCİ KOPYASI.
  // Neden kopya: /api/brand yanıtı gelene kadar geçen ilk karede ve API'ye hiç ulaşılamadığında
  // DEFAULT_BRAND uygulanıyordu; adı "SmartERP" olduğu için CX adresi kendini SmartERP sanıp
  // o ürünün kimliğiyle açılıyordu (sekme başlığı, logo, karşılama metni). Değerler sunucudaki
  // kayıtla AYNI kalmalı — burada yalnız ilk kare için gereken alanlar tutulur.
  if (sub === 'cxcentral') {
    return {
      ...DEFAULT_BRAND,
      subdomain: 'cxcentral',
      displayName: 'ELPO CX',
      tagline: 'AdBlue, cam suyu ve otomatik yıkama satış takibi',
      primaryColor: '#0091b3',
      accentColor: '#FF6A13',
      logo: '/brand/cxcentral/logo.png',
      // Favicon TUM Elpo urunlerinde AYNI dosya (bkz. DEFAULT_BRAND) — canli marka kaydi da
      // /favicon.ico donuyor. Dosyada duran /brand/cxcentral/favicon.png yazilsaydi ilk karede
      // bir ikon, API cevabi gelince baskasi gorunur, sekme ikonu gozle gorulur sekilde degisirdi.
      favicon: '/favicon.ico',
      homeRoute: '/cxcentral',
      loginGreeting: "ELPO CX'e hoş geldiniz",
      loginSubtitle: 'AdBlue, cam suyu ve otomatik yıkama satışları tek merkezde',
      footerText: '© ELPO CX · ElpoCore platformunda',
      loginFeatures: [
        'Cihazlardan otomatik satış toplama',
        'Tank seviyesi ve dolum takibi',
        'Üretici / dağıtıcı / istasyon kapsamlı görünürlük',
        'Gelir paylaşımı sözleşmeleri',
        'Gerçek zamanlı ünite sağlığı',
      ],
      panelTag: 'Merkezi İstasyon Yönetim Portalı',
      loginPanel: 'branded',
      navIsolated: true,
      formStyle: 'underline',
    };
  }
  // ElpoCore'un kendi adresi bir ÜRÜN değil, platformun yönetim konsoludur: uygulamalar, metadata,
  // tema ve tasarım araçları burada yaşar. Ürün ekranları kendi subdomain'inde açılır.
  if (sub === 'elpocore') {
    return {
      ...DEFAULT_BRAND,
      subdomain: 'elpocore',
      displayName: 'ElpoCore',
      tagline: 'Uygulama platformu',
      primaryColor: '#003B5C',
      accentColor: '#0091B3',
      homeRoute: '/admin/apps',
      platformKonsolu: true,
      loginGreeting: "ElpoCore'a hoş geldiniz",
      loginSubtitle: 'Uygulama, metadata ve tasarım yönetimi',
      footerText: '© ElpoCore · Uygulama platformu',
    };
  }
  if (sub === 'smarterp') {
    return {
      ...DEFAULT_BRAND,
      subdomain: 'smarterp',
      displayName: 'SmartERP',
      tagline: 'Yapay zeka destekli ERP',
      primaryColor: '#1e3a8a',
      accentColor: '#06b6d4',
      // Onceki deger uydurma bir SVG idi: mor gradient (#8b5cf6/#7c3aed) uzerine elle
      // cizilmis "S" harfi. Marka disi ve AI-izi; gercek ELPO amblemiyle degistirildi
      // (kullanici 15.09.2026: "o sacma sapan S harfi olan ne varsa hepsini sil").
      logo: '/elpo-logo-full.png',
      favicon: '/elpo-icon.png',
      homeRoute: '/',
    };
  }
  if (sub === 'eportal') {
    return {
      ...DEFAULT_BRAND,
      subdomain: 'eportal',
      displayName: 'E-Entegratör',
      // Tek başına satılan ürün (CX Central gibi): SmartERP hub'ı ürün ana sayfasına yönlenir, menüde
      // yalnız ürünün grupları kalır, store'daki bayat "aktif app" (smarterp) dikkate alınmaz.
      navIsolated: true,
      tagline: 'e-Fatura, e-İrsaliye, e-Arşiv — tek portal',
      primaryColor: '#0091B3',
      accentColor: '#FF6A13',
      homeRoute: '/einvoice/ebelge-dashboard',
      loginGreeting: 'E-Entegratör’e hoş geldiniz',
      loginSubtitle: 'e-Fatura, e-İrsaliye, e-Arşiv, e-SMM — GİB uyumlu tek portal',
      footerText: '© E-Entegratör · ElpoCore platformunda',
      loginFeatures: [
        'e-Fatura / e-Arşiv gönder-al',
        'e-İrsaliye düzenle ve gönder',
        'e-SMM (serbest meslek makbuzu)',
        'GİB / entegratör uyumlu — UBL 2.1 doğrulama',
        'Çoklu mükellef ve kontör yönetimi',
      ],
      sidebar: EPORTAL_SIDEBAR,
      integratorOnboarding: true,
      panelTag: 'E-Entegratör',
    };
  }
  return DEFAULT_BRAND;
}

let brandCache: BrandConfig | null = null;
let brandPromise: Promise<BrandConfig> | null = null;

// Sunucu appsettings'i deploy'da korunduğundan yeni menü girişleri config'e geç ulaşır.
// Yıkamatik menü girişlerini istemci tarafında garanti et — config'de varsa tekrar ekleme.
function ensureYikamatikSidebar(brand: BrandConfig): BrandConfig {
  if (brand.subdomain !== 'yikamatik') return brand;

  const sidebar = [...brand.sidebar];

  // İşlemler — Randevular'dan sonra
  if (!sidebar.some((i) => i.path === '/yikamatik/operations')) {
    const idx = sidebar.findIndex((i) => i.path === '/yikamatik/appointments');
    sidebar.splice(idx >= 0 ? idx + 1 : sidebar.length, 0, {
      label: 'İşlemler', icon: 'Car', path: '/yikamatik/operations', entity: null,
      subtitle: 'Plakadan geçmiş, durum, kaplama uyarısı', badge: null, children: null,
    });
  }

  // Kabinler — WashBay katalog yönetimi (kullanıcı kendi kabinlerini ekler/düzenler)
  if (!sidebar.some((i) => i.entity === 'industry-autowash/WashBay')) {
    sidebar.push({
      label: 'Kabinler', icon: 'LayoutGrid', path: null, entity: 'industry-autowash/WashBay',
      subtitle: 'Yıkama kabinlerini tanımla', badge: null, children: null,
    });
  }

  return { ...brand, sidebar };
}

// eportal API'den sidebar boş gelir (server config'de tanımlı değil) — kendi e-Belge menüsünü
// istemci tarafında garanti et + home route'u e-Belge özetine al.
function ensureEportalSidebar(brand: BrandConfig): BrandConfig {
  if (brand.subdomain !== 'eportal') return brand;
  const sidebar = brand.sidebar.length > 0 ? brand.sidebar : EPORTAL_SIDEBAR;
  const homeRoute = brand.homeRoute === '/admin/dashboard' ? '/einvoice/ebelge-dashboard' : brand.homeRoute;
  // Sunucu brand'i bu alanları döndürmese de eportal kimliğini garanti et.
  const panelTag = brand.panelTag ?? 'E-Entegratör';
  const integratorOnboarding = brand.integratorOnboarding ?? true;
  return { ...brand, sidebar, homeRoute, panelTag, integratorOnboarding };
}

/**
 * Sekme ikonu + sayfa adı. Ürünün kimliği ilk karede görünsün diye marka gelir gelmez çağrılır.
 * index.html ikonu `rel="shortcut icon"`; yalnız `rel="icon"` aranınca bulunamayıp ikinci bir
 * link ekleniyordu ve tarayıcı ilk gördüğünü kullandığı için sekmede eski ikon kalıyordu.
 */
function markaKimliginiUygula(brand: BrandConfig): void {
  if (typeof document === 'undefined') return;

  // FAVICON ÜRÜNE ÖZELDİR (kullanıcı kararı 2026-09-05): ELPO CX'in sekmesinde CX amblemi,
  // SmartERP'in sekmesinde ELPO'nun yuvarlak "e"si olur. Markanın kendi ikonu yoksa ELPO
  // varsayılanına düşülür.
  //
  // TARİHÇE — bu kod iki kez yanlış yerden çözüldü, ikisi de burada kayıtlı olsun:
  // (1) 2026-08-31: sunucudaki marka kaydı SmartERP için bir favicon döndürüyordu ve sekmedeki
  //     ELPO ikonu değişiyordu; çözüm olarak "hepsinde aynı ikon" kuralı KODA gömüldü.
  // (2) 2026-09-04: CX işi sırasında PAYLAŞILAN favicon.ico/favicon-32.png dosyalarının içeriği
  //     CX amblemiyle DEĞİŞTİRİLDİ. Kural hâlâ "hepsi aynı" diyordu ama o "aynı" ikon artık
  //     CX'ti — SmartERP de CX ikonuyla açılıyordu (kullanıcı bildirdi).
  // Kök sorun ikisinde de aynı: ürüne özel bir şey PAYLAŞILAN dosyaya yazıldı. Doğrusu, ikonu
  // markadan okumak ve paylaşılan dosyaya dokunmamaktır.
  // ELPO varsayilani ARTIK AMBLEM PNG'si: favicon.ico icerigi gecmiste urune ozel ikonla
  // degistirildigi icin ne oldugu belirsizdi; elpo-icon.png markanin kendi amblemi ve
  // baska hicbir urun onu ezmiyor (kullanici karari 14.09.2026: "favicon'a ELPO logosunu koy").
  const ELPO_FAVICON = `${import.meta.env.BASE_URL}elpo-icon.png`;
  const ikon = brand.favicon && brand.favicon.trim() !== '' ? brand.favicon : ELPO_FAVICON;
  const mevcutlar = document.querySelectorAll<HTMLLinkElement>(
    'link[rel="icon"], link[rel="shortcut icon"], link[rel="apple-touch-icon"]',
  );
  if (mevcutlar.length === 0) {
    const yeni = document.createElement('link');
    yeni.rel = 'icon';
    yeni.href = ikon;
    document.head.appendChild(yeni);
  } else {
    mevcutlar.forEach((link) => { link.href = ikon; });
  }

  if (brand.displayName) document.title = brand.displayName;
}

export const brandService = {
  async get(): Promise<BrandConfig> {
    if (brandCache) return brandCache;
    if (brandPromise) return brandPromise;
    // Marka login metni (karşılama/alt başlık) UI ile AYNI dilde gelsin: backend'e ?lang geçir.
    // Dil tespiti i18n.ts ile BİREBİR aynı olmalı: YALNIZ kullanıcının kendi seçimi (localStorage),
    // yoksa 'tr'. navigator BİLEREK kullanılmaz — i18n de kullanmıyor (order: ['localStorage']).
    // Buradaki navigator ölçümü tam da önlemeye çalıştığı karma ekranı ÜRETİYORDU: kurumsal
    // makinelerin tarayıcısı "en" olduğu için login'de form Türkçe (E-posta/Şifre/Giriş Yap) ama
    // marka metinleri İngilizce ("Welcome to ELPO CX", özellik listesi) çıkıyordu.
    // i18n.language KULLANILMAZ — init bitmeden fallback değerinde olabiliyor.
    const lsLang = (typeof localStorage !== 'undefined') ? (localStorage.getItem('i18nextLng') || '') : '';
    const lang = (lsLang || 'tr').split('-')[0];
    brandPromise = apiClient.get<BrandConfig>('/api/brand', { params: { lang } })
      .then((r) => {
        // Sunucu brand'i cache'te yoksa generic (subdomain null) döner. Host'tan bilinen bir
        // subdomain çözebiliyorsak (eportal/yikamatik/smarterp) yerel marka kimliğini KORU —
        // aksi halde branded login generic'e düşer (logo/sekme/karşılama kaybolur).
        const local = brandFromSubdomain();
        const base = (!r.data.subdomain && local.subdomain) ? local : r.data;
        // "Burası platform konsolu mu" bilgisi ADRESTEN gelir, sunucudaki marka kaydından değil:
        // o kayıt logo/renk/karşılama gibi görsel kimliği taşır, adresin ne olduğunu bilmez.
        // Sunucu elpocore markasını döndürdüğünde base=r.data oluyor ve yerel daldaki bayrak
        // düşüyordu; sonuç olarak platform adresinde SmartERP'in CRM/Satış/Stok menüsü diziliyordu.
        const platformlu = local.platformKonsolu
          ? { ...base, platformKonsolu: true, homeRoute: local.homeRoute }
          : base;
        const b = ensureEportalSidebar(ensureYikamatikSidebar(platformlu));
        brandCache = b;
        // Form bölüm başlığı stili marka verisinden (SmartŞarj dili isteyen ürünler "underline").
        registerFormSectionStyle(b.formStyle === 'underline' ? 'underline' : 'accent');
        // Sekme ikonu ve adı marka ÇÖZÜLÜR ÇÖZÜLMEZ uygulanır. Önceden yalnız AppLayout mount'unda
        // uygulanıyordu; login ekranında AppLayout olmadığı için ELPO CX'te bile sekmede ELPO'nun
        // genel favicon.ico'su duruyordu (index.html'deki sabit ikon).
        markaKimliginiUygula(b);
        return b;
      })
      .catch((err) => {
        // Sessiz fallback UC sorun uretiyordu (denetim bulgusu 2026-08-31):
        // 1) log yok — /api/brand tamamen cokse kimse bilmiyordu,
        // 2) brandCache atanmiyor — sonraki her cagri basarisiz istegi YENIDEN atiyordu,
        // 3) markaKimliginiUygula cagrilmiyor — favicon kurali ve form bolum stili uygulanmiyordu.
        console.warn('Marka bilgisi alınamadı; adresten çözülen kimlik kullanılıyor', err);
        const yerel = brandFromSubdomain();
        brandCache = yerel;
        registerFormSectionStyle(yerel.formStyle === 'underline' ? 'underline' : 'accent');
        markaKimliginiUygula(yerel);
        return yerel;
      })
      .finally(() => { brandPromise = null; });
    return brandPromise;
  },

  cached(): BrandConfig {
    return brandCache ?? brandFromSubdomain();
  },

  reset(): void {
    brandCache = null;
    brandPromise = null;
  },

  /**
   * "smarterp/Customer" key'i için brand override label döner; yoksa null.
   */
  entityLabel(brand: BrandConfig, appName: string, entityName: string, locale = aktifDil()): string | null {
    const key = `${appName}/${entityName}`;
    return brand.entityLabels[key]?.[locale] ?? brand.entityLabels[key]?.en ?? null;
  },
};
